// This is the SubmitsTab component, which will display the submits for the user and allow for the user to edit dels as needed.

import React, { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { shallow } from "zustand/shallow";
import {
  parseHomeRoute,
  buildCanonicalHomePath,
} from "../../../../services/RouteUtils";
import {
  startTask,
  getTaskStatus,
  downloadTaskResult,
} from "../../../../api/TasksApi";
import { useAppStore } from "../../../../store/appStore";
import { copyDel } from "../../../../api/DelApi";
import { getLocalDateString } from "../../../../utils/dateUtils";
import { useUserState } from "../../../../context/user";
import { useFilters } from "../../../../hooks/useFilters";
import { useFilterMenu } from "../../../../hooks/useFilterMenu";
import { filtersInitialState } from "../../../../store/shared/filters/filtersState";
// Components
import DelForm from "../../../delForm/DelForm";
import {
  ViewDelButton,
  CopyDelButton,
  RefreshDelListsButton,
  FilterButton,
  DownloadPdfButton,
} from "../buttons/DelActions";
import { DelFilterMenu } from "../../../common/FilterMenu";
import { DelSubmittedTable } from "../del_table";
import styles from "./SubmitsTab.module.css";

export const SubmitsTab = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const showNotification = useAppStore(
    (s) => s.notificationActions.showNotification,
  );
  // User Info
  const user = useUserState().currentUser;
  // Employees
  const employees = useAppStore((s) => s.employees.list, shallow);
  // Terminated employees
  const allTerminatedEmployees = useAppStore(
    (s) => s.employees.terminatedList,
    shallow,
  );
  const allSearchEmployees = [...employees, ...allTerminatedEmployees];
  // Projects
  const allProjectInfo = useAppStore((s) => s.projects.list, shallow);
  // Inactive projects
  const allInactiveProjects = useAppStore(
    (s) => s.projects.inactiveList,
    shallow,
  );
  // Loading UI state
  const { isActive } = useAppStore((s) => s.loading, shallow);
  // Loading UI actions
  const showLoading = useAppStore((s) => s.loadingActions.showLoading);
  const hideLoading = useAppStore((s) => s.loadingActions.hideLoading);

  // Submits List State
  const submits = useAppStore((s) => s.delLists.submits.items, shallow);
  const totalCount = useAppStore((s) => s.delLists.submits.totalCount, shallow);
  const currentPage = useAppStore(
    (s) => s.delLists.submits.currentPage,
    shallow,
  );
  const selectedSubmit = useAppStore(
    (s) => s.delLists.submits.selectedSubmit,
    shallow,
  );
  const pageSize = useAppStore((s) => s.delLists.submits.pageSize, shallow);

  // DEL UI States
  const { showDelForm } = useAppStore((s) => s.submitsTabUI, shallow);
  const activeDel = useAppStore((s) => s.activeDelSubmits, shallow);
  const submitDelActions = useAppStore(
    (s) => s.activeDelSubmitsActions,
    shallow,
  );

  // App Store Actions
  // Workflow Actions
  const fetchDel = useAppStore((s) => s.activeDelSubmitsActions.fetchDel);

  // Specific DEL UI actions
  const setShowDelForm = useAppStore(
    (s) => s.submitsTabUIActions.setShowDelForm,
  );

  // Handling Page UI actions
  const pageSizeChange = useAppStore(
    (s) => s.delListsActions.setSubmitsPageSize,
  );
  const setDraftsPage = useAppStore((s) => s.delListsActions.setDraftsPage);
  const setSubmitsPage = useAppStore((s) => s.delListsActions.setSubmitsPage);
  const selectSubmitRow = useAppStore(
    (s) => s.delListsActions.setSelectedSubmit,
  );
  // Refreshing DEL Lists, both Drafts and Submits.
  const refreshDraftsDelLists = useAppStore(
    (s) => s.delListsActions.fetchDraftsPage,
  );
  const refreshSubmitsDelLists = useAppStore(
    (s) => s.delListsActions.fetchSubmitsPage,
  );

  // Local filter state (resets on unmount)
  const {
    filters: submitsFilters,
    updateFilter: updateFilterAction,
    clearFilters: clearFiltersAction,
  } = useFilters({ status: "" });
  // Filter menu UI state
  const {
    showFilterMenu,
    toggleFilterMenu: handleToggleFilterMenu,
    filterPhaseList,
    setFilterPhaseList: filterPhaseActionFunction,
    filterMenuRef,
  } = useFilterMenu();

  // Applying Filters
  const handleApplyFilters = async () => {
    showLoading("Loading DELs...");
    setSubmitsPage({ currentPage: 1, pageSize });
    await refreshSubmitsDelLists(user.employee_id, submitsFilters);
    hideLoading();
  };
  // Clearing Filters
  const handleClearFilters = async () => {
    clearFiltersAction();
    setSubmitsPage({ currentPage: 1, pageSize });
    showLoading("Loading DELs...");
    await refreshSubmitsDelLists(
      user.employee_id,
      filtersInitialState(),
    );
    hideLoading();
  };

  // Function to handle the view del button click
  const handleShowDelSubmit = async () => {
    if (!selectedSubmit) {
      window.alert("Please select a DEL to view.");
      return;
    }
    const target = buildCanonicalHomePath({
      tab: "submitted",
      delId: selectedSubmit.id,
      action: "view",
      open: true,
    });
    navigate(target);
  };

  // Fetchs a Submitted DEL to view, same as above
  const handleDoubleClick = async (del) => {
    // If user double clicks on a del, we need to handle the double click
    const target = buildCanonicalHomePath({
      tab: "submitted",
      delId: del.id,
      action: "view",
      open: true,
    });
    navigate(target);
  };

  // Function to handle the copy del button click
  const handleCopyDel = async () => {
    if (!selectedSubmit) {
      window.alert("Please select a DEL to copy.");
      return;
    }
    try {
      showLoading("Copying DEL...");
      const resp = await copyDel(selectedSubmit.id, user, "submits");
      const { del_id, del_creation_date, summary } = resp || {};
      if (!del_id) {
        throw new Error("Copy failed");
      }
      // Upsert into Drafts list (not this tab's list)
      if (summary) {
        useAppStore.getState().delListsActions.upsertDraftLocal(summary);
      } else {
        useAppStore.getState().delListsActions.upsertDraftLocal({
          id: del_id,
          del_creation_date: del_creation_date || getLocalDateString(),
          updated: getLocalDateString(),
          project_number: "-",
        });
      }
      // Notify user using app notification modal
      useAppStore.getState().notificationActions.showNotification({
        type: "success",
        title: "DEL Copied",
        message: "Your DEL has been copied. You can edit it in the Drafts tab.",
      });
      hideLoading();
    } catch (error) {
      hideLoading();
      useAppStore.getState().notificationActions.showNotification({
        type: "error",
        title: "Error Copying DEL",
        message: "Please try again later.",
      });
      console.error("Error copying DEL:", error);
    }
  };

  // Handlers for pagination
  // Handle change in page size
  const handlePageSizeChange = async (e) => {
    showLoading("Loading DELs...");
    const newPageSize = parseInt(e.target.value, 10);
    pageSizeChange(newPageSize);
    await refreshSubmitsDelLists(user.employee_id, submitsFilters);
    hideLoading();
  };

  const handlePageChange = async (direction) => {
    showLoading("Loading DELs...");

    let newPage;
    if (direction === "next") {
      newPage = currentPage + 1;
    } else {
      newPage = currentPage - 1;
    }
    setSubmitsPage({ currentPage: newPage, pageSize });
    await refreshSubmitsDelLists(user.employee_id, submitsFilters);
    hideLoading();
  };

  const handleSelectSubmitRow = (del) => {
    selectSubmitRow(del);
  };

  // Add refresh handler
  const handleRefreshDelLists = async () => {
    showLoading("Refreshing DELs...");
    const draftsPageSize = useAppStore.getState().delLists.drafts.pageSize;
    setDraftsPage({ currentPage: 1, pageSize: draftsPageSize });
    setSubmitsPage({ currentPage: 1, pageSize });
    await refreshDraftsDelLists(user.employee_id);
    await refreshSubmitsDelLists(user.employee_id, submitsFilters);
    hideLoading();
  };

  // Deep-link handler - single source of truth
  const processedRef = useRef(null);
  useEffect(() => {
    const run = async () => {
      const parsed = parseHomeRoute(location.pathname, location.search);
      const idStr = parsed.delId;
      const action = parsed.action || (idStr ? "view" : null);
      const wantsOpen = !!parsed.open;
      const key = `submitted:${action || ""}:${idStr || ""}:${
        wantsOpen ? "1" : "0"
      }`;
      if (processedRef.current === key) return;
      processedRef.current = key;

      try {
        // If URL points to list view (no del_id, no action), ensure form is closed
        if (!idStr && !action) {
          if (showDelForm) setShowDelForm(false);
          return;
        }

        // Selection-only
        if (idStr && !wantsOpen) {
          selectSubmitRow({ id: idStr });
          setShowDelForm(false);
          return;
        }

        // Open view
        if (idStr && wantsOpen && action === "view") {
          // Close form first to prevent rendering stale/null data during fetch
          if (showDelForm) setShowDelForm(false);
          showLoading("Loading DEL...");
          const res = await fetchDel("view", idStr);
          if (res && res.ok) {
            setShowDelForm(true);
          } else {
            setShowDelForm(false);
            try {
              useAppStore.getState().notificationActions.showNotification({
                type: "error",
                title: "Error Loading DEL",
                message: "DEL not found or could not be loaded.",
              });
            } catch {}
          }
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("Failed to process Submits deep link", err);
      } finally {
        hideLoading();
      }
    };
    run();
  }, [
    location.pathname,
    location.search,
    fetchDel,
    selectSubmitRow,
    setShowDelForm,
    showLoading,
    hideLoading,
    showDelForm,
  ]);

  // On unmount of SubmitsTab, ensure the Submits DelForm is hidden to keep routing clean
  useEffect(() => {
    return () => {
      try {
        setShowDelForm(false);
      } catch {}
    };
  }, [setShowDelForm]);

  // Download pdf(s)
  // State items, like list of checked DEL ids
  const toggleSelectedSubmit = useAppStore(
    (s) => s.delListsActions.toggleSelectedSubmit,
  );
  const selectedSubmitIds = useAppStore(
    (s) => s.delLists.submits.selectedSubmitIds,
  );
  const handleDownloadPdfs = async () => {
    if (selectedSubmitIds.length === 0) {
      showNotification({
        type: "error",
        title: "No DELs selected",
        message:
          "Please select at least one DEL checkbox to start the download.",
      });
      return;
    }
    if (selectedSubmitIds.length > 50) {
      showNotification({
        type: "error",
        title: "Too many DELs selected",
        message:
          "You can only download up to 50 DELs at a time. Please reduce your selection.",
      });
      return;
    }
    showLoading("Downloading DELs...");
    try {
      const { data: start } = await startTask("del_ops.create_pdfs", {
        del_ids: selectedSubmitIds,
      });
      const taskId = start?.task_id;
      if (!taskId) throw new Error("Failed to start PDF task");

      let attempts = 0;
      let ready = false;
      let failed = false;
      let errorMessage = null;
      let lastStatus = null;
      // 150 attempts x 2s = 5 minutes for bulk PDFs (up to 50 DELs)
      const maxAttempts = 150;
      while (!ready && attempts < maxAttempts) {
        await new Promise((r) => setTimeout(r, 2000));
        const { data: status } = await getTaskStatus(taskId);
        lastStatus = status;
        ready = !!status?.ready;
        if (ready && !status?.ok) {
          failed = true;
          errorMessage = status?.error || "Task failed";
          break;
        }
        attempts += 1;
      }
      if (!ready)
        throw new Error(
          "Timed out waiting for PDFs. The task may still be running.",
        );
      if (failed) throw new Error(errorMessage || "Task failed");

      const res = await downloadTaskResult(taskId, {
        s3DownloadUrl: lastStatus?.s3_download_url,
        filename: lastStatus?.filename,
      });
      if (res) {
        const blob = new Blob([res.data], {
          type: res.headers["content-type"] || "application/zip",
        });
        const cd = res.headers["content-disposition"] || "";
        const match = /filename\*?=(?:UTF-8''|")?([^";]+)/i.exec(cd);
        const filename = match ? decodeURIComponent(match[1]) : "DELs.zip";
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        a.click();
        window.URL.revokeObjectURL(url);
      }
    } catch (e) {
      showNotification({
        type: "error",
        title: "PDF Download Failed",
        message: e.message || "Unknown error",
      });
    } finally {
      hideLoading();
    }
  };

  // Download a single pdf
  const handleDownloadPdf = async (submitId) => {
    if (!submitId) {
      showNotification({
        type: "error",
        title: "No DEL selected",
      });
      return;
    }
    showLoading("Downloading DEL...");
    try {
      const { data: start } = await startTask("del_ops.create_single_pdf", {
        del_id: submitId,
      });
      const taskId = start?.task_id;
      if (!taskId) throw new Error("Failed to start PDF task");

      let attempts = 0;
      let ready = false;
      let failed = false;
      let errorMessage = null;
      let lastStatus = null;
      while (!ready && attempts < 60) {
        await new Promise((r) => setTimeout(r, 2000));
        const { data: status } = await getTaskStatus(taskId);
        lastStatus = status;
        ready = !!status?.ready;
        if (ready && !status?.ok) {
          failed = true;
          errorMessage = status?.error || "Task failed";
          break;
        }
        attempts += 1;
      }
      if (!ready) throw new Error("Timed out waiting for PDF");
      if (failed) throw new Error(errorMessage || "Task failed");

      const res = await downloadTaskResult(taskId, {
        s3DownloadUrl: lastStatus?.s3_download_url,
        filename: lastStatus?.filename,
      });
      if (res) {
        const blob = new Blob([res.data], {
          type: res.headers["content-type"] || "application/pdf",
        });
        const cd = res.headers["content-disposition"] || "";
        const match = /filename\*?=(?:UTF-8''|")?([^";]+)/i.exec(cd);
        const filename = match ? decodeURIComponent(match[1]) : "DEL.pdf";
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        a.click();
        window.URL.revokeObjectURL(url);
      }
    } catch (e) {
      showNotification({
        type: "error",
        title: "PDF Download Failed",
        message: e.message || "Unknown error",
      });
    } finally {
      hideLoading();
    }
  };

  const handleClearSelectedSubmitIds = useAppStore(
    (s) => s.delListsActions.clearSelectedSubmitIds,
  );

  // Monitor selection count and warn if > 50
  useEffect(() => {
    if (selectedSubmitIds.length > 50) {
      showNotification({
        type: "warning",
        title: "Selection limit exceeded",
        message: `You have selected ${selectedSubmitIds.length} DELs. You can only download 50 at a time. Please deselect some DELs before downloading.`,
      });
    }
  }, [selectedSubmitIds, showNotification]);

  return (
    <>
      {showDelForm ? (
        <>
          <DelForm
            activeDelTabInfo={activeDel}
            actionFunctions={submitDelActions}
            setShowDelForm={() => setShowDelForm(false)}
            tableName="submitted"
            refreshDelListFunction={handleRefreshDelLists}
            copyDelFunction={handleCopyDel}
          />
        </>
      ) : (
        <>
          <div className={styles.tableActions}>
            <ViewDelButton viewFunction={handleShowDelSubmit} />
            <CopyDelButton copyFunction={handleCopyDel} />
          </div>

          <div className={styles.headerWithRefresh}>
            <h2>Submitted DELs</h2>
            <div
              style={{
                position: "relative",
                display: "flex",
                alignItems: "center",
                gap: 8,
              }}
              ref={filterMenuRef}
            >
              <DownloadPdfButton
                onClick={handleDownloadPdfs}
                disabled={isActive}
              />
              <FilterButton filterFunction={handleToggleFilterMenu} />
              {showFilterMenu && (
                <DelFilterMenu
                  delTab="submitted"
                  filters={submitsFilters}
                  updateFilterAction={updateFilterAction}
                  onApplyFilters={handleApplyFilters}
                  onClearFilters={handleClearFilters}
                  allProjectInfo={allProjectInfo}
                  allInactiveProjects={allInactiveProjects}
                  allEmployees={allSearchEmployees}
                  filterPhaseList={filterPhaseList}
                  filterPhaseActionFunction={filterPhaseActionFunction}
                />
              )}
            </div>
            <RefreshDelListsButton
              refreshFunction={handleRefreshDelLists}
              isLoading={isActive}
            />
          </div>
          <div className={styles.tableWrapper}>
            {submits.length > 0 ? (
              <DelSubmittedTable
                dels={submits.slice(0, pageSize)}
                onRowSelect={handleSelectSubmitRow}
                onDoubleClick={handleDoubleClick}
                selectedDelId={selectedSubmit?.id || null}
                selectedIds={selectedSubmitIds}
                onToggleSelect={toggleSelectedSubmit}
                onDownloadSingle={handleDownloadPdf}
                clearSelectedSubmitIds={handleClearSelectedSubmitIds}
              />
            ) : (
              <p className={styles.noData}>No submitted DELs to display</p>
            )}
          </div>
          <div className={styles.paginationControls}>
            <div className={styles.paginationLeftControls}>
              <button
                onClick={() => handlePageChange("prev")}
                disabled={currentPage === 1 || isActive}
              >
                Previous
              </button>
              <button
                onClick={() => handlePageChange("next")}
                disabled={currentPage * pageSize >= totalCount || isActive}
              >
                Next
              </button>
              <div className={styles.pageSizeSelector}>
                <label htmlFor="pageSize">Rows:</label>
                <select
                  id="pageSize"
                  className={styles.pageSizeSelect}
                  value={pageSize}
                  onChange={handlePageSizeChange}
                >
                  <option value="10">10</option>
                  <option value="25">25</option>
                  <option value="50">50</option>
                </select>
              </div>
            </div>
            <span>
              Showing {Math.min((currentPage - 1) * pageSize + 1, totalCount)} -{" "}
              {Math.min(currentPage * pageSize, totalCount)} of {totalCount}{" "}
              submits
            </span>
          </div>
        </>
      )}
    </>
  );
};
